// One editable block per paragraph (plan D3, design §6.1). The whole editor is
// the rule list below and nothing else:
//   Return          split at the caret (newline inside an unclosed code fence)
//   Shift+Return    newline
//   Backspace at 0  merge into the previous paragraph
//   Arrow up/down   at the first/last line, move to the neighbour
//   Paste           multi-block text becomes several paragraphs
//   Undo/redo       the document's own stack, not the text area's
// No toolbar, no shortcuts, no syntax hiding in the active block, no
// cross-block text selection (the gutter selects ranges).
//
// Every change leaves as an operation through the store, which is what the
// outbox sends and what undo inverts.

import {
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { renderBlock } from "../markdown/render";
import { splitMarkdown } from "../markdown/split";
import type { DocumentStore } from "../store/documentStore";
import { type DocState, type Op, type ParagraphId, afterForIndex, newId } from "../store/ops";
import { useGutter } from "./useGutter";

interface Props {
  doc: DocState;
  store: DocumentStore;
  selection: ParagraphId[];
  onSelectionChange: (ids: ParagraphId[]) => void;
  /** What is selected in the body, or null when nothing is. A review rounds a
   *  mid-paragraph selection up to its paragraph (design §6.2), and this is
   *  what it rounds; a copy takes the characters as they are. A bare caret
   *  does not count: it would make reviewing the whole document unreachable
   *  while writing, since there is no way to put the caret nowhere. */
  onTextSelection?: (selection: TextSelection | null) => void;
  /** Bumped when something outside the editor has cleared the selection. The
   *  editor collapses the caret, because the browser goes on painting a
   *  selection in a text area that no longer has focus. */
  clearText?: number;
  /** Paragraphs matching the current find, and the one being looked at. */
  found?: ParagraphId[];
  lookingAt?: ParagraphId | null;
  /** Per paragraph that is the last one a comment is about: how many comments
   *  sit under it, live and noticed quietly (design §7.2). A line below the
   *  paragraph carries the count, and pressing it opens that paragraph's
   *  tray. */
  trays?: Map<ParagraphId, number>;
  openTray?: ParagraphId | null;
  onToggleTray?: (id: ParagraphId) => void;
  /** The tray's contents, rendered under the row while it is open. */
  renderTray?: (id: ParagraphId) => React.ReactNode;
}

type Caret = number | "end";

/** What is selected inside one paragraph. The characters travel with the
 *  offsets because copying wants exactly them, while reviewing rounds the
 *  whole thing up to the paragraph (design §6.2). */
export interface TextSelection {
  id: ParagraphId;
  start: number;
  end: number;
  text: string;
}

export function Editor({
  doc,
  store,
  selection,
  onSelectionChange,
  onTextSelection,
  clearText,
  found,
  lookingAt,
  trays,
  openTray,
  onToggleTray,
  renderTray,
}: Props) {
  const [active, setActive] = useState<ParagraphId | null>(null);
  const activeRef = useRef<ParagraphId | null>(null);
  activeRef.current = active;
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  // The last range handed upward, so an unchanged one is not handed up twice.
  const reported = useRef<TextSelection | null>(null);
  const rows = useRef<(HTMLElement | null)[]>([]);

  // Commit state synchronously and move focus inside the same input event.
  // iPadOS only keeps the keyboard up if the next focus happens before the
  // event handler returns, which is why this is flushSync and not an effect.
  const activate = useCallback((id: ParagraphId, caret: Caret, mutate?: () => void) => {
    flushSync(() => {
      mutate?.();
      setActive(id);
    });
    const ta = textarea.current;
    if (!ta) return;
    const at = caret === "end" ? ta.value.length : Math.min(caret, ta.value.length);
    ta.focus();
    ta.setSelectionRange(at, at);
    autosize(ta);
  }, []);

  useLayoutEffect(() => {
    if (textarea.current) autosize(textarea.current);
  }, [active, doc]);

  useEffect(() => {
    if (!lookingAt) return;
    const at = doc.order.indexOf(lookingAt);
    rows.current[at]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [lookingAt, doc.order]);

  // Undo can remove the paragraph that holds the cursor.
  useEffect(() => {
    if (active && !(active in doc.text)) setActive(null);
  }, [active, doc]);

  // What is selected inside the active block, reported upward so a review can
  // round it up to this paragraph (design §6.2).
  //
  // `selectionchange` on the document rather than React's `onSelect`: it is
  // the signal the browser actually emits for a textarea, it covers the
  // keyboard and the iPadOS selection handles as well as the mouse, and it
  // does not depend on React's synthetic-event polyfill. Blur is deliberately
  // not treated as the end of a selection — pressing Review blurs the textarea,
  // and the scope has to outlive the press — so the value is read from the
  // element, which keeps it.
  useEffect(() => {
    if (!active) {
      reported.current = null;
      onTextSelection?.(null);
      return;
    }
    const report = () => {
      const ta = textarea.current;
      if (!ta) return;
      const { selectionStart: from, selectionEnd: to } = ta;
      const next: TextSelection | null =
        from === to ? null : { id: active, start: from, end: to, text: ta.value.slice(from, to) };
      // `selectionchange` fires on every caret move, and the value is an
      // object now rather than an id, so reporting it unconditionally would
      // re-render the screen above on every arrow key. Only a genuinely
      // different range goes up.
      const was = reported.current;
      const same =
        next === null
          ? was === null
          : was !== null && was.id === next.id && was.start === next.start && was.end === next.end;
      if (same) return;
      reported.current = next;
      onTextSelection?.(next);
    };
    report();
    document.addEventListener("selectionchange", report);
    return () => document.removeEventListener("selectionchange", report);
  }, [active, onTextSelection]);

  // Something outside the editor cleared the selection. Focus has already
  // moved to whatever was pressed, so `document.activeElement` is no help;
  // the caret is collapsed on the element directly.
  useEffect(() => {
    if (clearText === undefined) return;
    const ta = textarea.current;
    if (ta) ta.setSelectionRange(ta.selectionEnd, ta.selectionEnd);
  }, [clearText]);

  // Cmd/Ctrl+Z anywhere in the document; the iPad keyboard's undo key arrives
  // as a beforeinput on the text area and is handled there.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) store.redo();
        else store.undo();
      } else if (key === "y") {
        e.preventDefault();
        store.redo();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [store]);

  const onBeforeInput = (e: FormEvent<HTMLTextAreaElement>) => {
    const type = (e.nativeEvent as InputEvent).inputType;
    if (type === "historyUndo") {
      e.preventDefault();
      store.undo();
    } else if (type === "historyRedo") {
      e.preventDefault();
      store.redo();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>, id: ParagraphId, index: number) => {
    if (e.nativeEvent.isComposing) return;
    const ta = e.currentTarget;
    const text = doc.text[id] ?? "";
    const { selectionStart: s, selectionEnd: en } = ta;

    if (e.key === "Enter" && !e.shiftKey) {
      if (insideOpenFence(text, s)) return;
      e.preventDefault();
      const second = newId();
      const ops: Op[] = [];
      if (s !== en) ops.push({ t: "set_text", id, text: text.slice(0, s) + text.slice(en) });
      ops.push({ t: "split", id, new_id: second, at: s });
      activate(second, 0, () => store.dispatchAll(ops));
      return;
    }

    if (e.key === "Backspace" && s === 0 && en === 0 && index > 0) {
      e.preventDefault();
      const prev = doc.order[index - 1]!;
      const seam = (doc.text[prev] ?? "").length;
      activate(prev, seam, () => store.dispatch({ t: "merge", survivor: prev, absorbed: id }));
      return;
    }

    if (e.key === "ArrowUp" && index > 0 && s === en && !text.slice(0, s).includes("\n")) {
      if (s === 0 || singleVisualLine(ta)) {
        e.preventDefault();
        activate(doc.order[index - 1]!, "end");
      }
      return;
    }

    if (
      e.key === "ArrowDown" &&
      index < doc.order.length - 1 &&
      s === en &&
      !text.slice(en).includes("\n")
    ) {
      if (en === text.length || singleVisualLine(ta)) {
        e.preventDefault();
        activate(doc.order[index + 1]!, 0);
      }
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>, id: ParagraphId) => {
    const pasted = e.clipboardData.getData("text/plain");
    if (!pasted.includes("\n\n")) return;
    e.preventDefault();
    const ta = e.currentTarget;
    const text = doc.text[id] ?? "";
    const before = text.slice(0, ta.selectionStart);
    const after = text.slice(ta.selectionEnd);
    const blocks = splitMarkdown(before + pasted + after);
    if (blocks.length === 0) return;

    const ops: Op[] = [{ t: "set_text", id, text: blocks[0]! }];
    let previous = id;
    for (const block of blocks.slice(1)) {
      const next = newId();
      ops.push({ t: "insert", id: next, after: previous, text: block });
      previous = next;
    }
    const lastText = blocks[blocks.length - 1]!;
    activate(previous, Math.max(0, lastText.length - after.length), () => store.dispatchAll(ops));
  };

  const onBlur = (id: ParagraphId) => {
    // Deactivate only if nothing else in the editor took focus. When focus
    // moves block to block, `activate` has already switched `active` by the
    // time this runs, so the stale blur is ignored.
    window.setTimeout(() => {
      if (activeRef.current === id && document.activeElement !== textarea.current) {
        setActive(null);
      }
    }, 0);
  };

  const gutter = useGutter(rows, {
    onTap: (i) => onSelectionChange([doc.order[i]!]),
    onRange: (from, to) => {
      const [a, b] = from <= to ? [from, to] : [to, from];
      onSelectionChange(doc.order.slice(a, b + 1));
    },
    onDrop: (from, to) => {
      const id = doc.order[from]!;
      store.dispatch({ t: "move", id, after: afterForIndex(doc.order, id, to) });
    },
  });

  const foundSet = useMemo(() => new Set(found ?? []), [found]);
  const selected = new Set(selection);
  let others = 0; // rows that are not the lifted one, for placing the drop line

  return (
    <div className="editor">
      {doc.order.map((id, i) => {
        const isLifted = gutter.lifted === i;
        const showDropBefore = gutter.lifted !== null && !isLifted && gutter.dropIndex === others;
        if (!isLifted) others++;
        const text = doc.text[id] ?? "";
        const isActive = active === id;
        const count = trays?.get(id) ?? 0;
        const cls = [
          "row",
          isActive && "active",
          selected.has(id) && "selected",
          isLifted && "lifted",
          foundSet.has(id) && "found",
          lookingAt === id && "looking-at",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div key={id}>
            {showDropBefore && <div className="drop-line" />}
            <div
              className={cls}
              data-paragraph-id={id}
              ref={(el) => {
                rows.current[i] = el;
              }}
            >
              <button
                type="button"
                className="gutter"
                aria-label={`Paragraph ${i + 1}`}
                {...gutter.handlers(i)}
              >
                {i + 1}
              </button>
              {isActive ? (
                <textarea
                  ref={textarea}
                  className="block source"
                  value={text}
                  rows={1}
                  spellCheck
                  autoCapitalize="sentences"
                  onChange={(e) => {
                    store.dispatch({ t: "set_text", id, text: e.target.value });
                    autosize(e.target);
                  }}
                  onBeforeInput={onBeforeInput}
                  onKeyDown={(e) => onKeyDown(e, id, i)}
                  onPaste={(e) => onPaste(e, id)}
                  onBlur={() => onBlur(id)}
                />
              ) : (
                <div
                  className={`block rendered${text.trim() === "" ? " empty" : ""}`}
                  onClick={() => {
                    onSelectionChange([]);
                    activate(id, "end");
                  }}
                  dangerouslySetInnerHTML={{
                    __html: text.trim() === "" ? "<p>&nbsp;</p>" : renderBlock(text),
                  }}
                />
              )}
            </div>
            {count > 0 && (
              <>
                {/* A seam in the prose rather than a badge on its shoulder:
                    the comments belong between this paragraph and the next,
                    so that is where the line and its count sit. */}
                <div className="tray-divider">
                  <button
                    type="button"
                    className="tray-open"
                    onClick={() => onToggleTray?.(id)}
                    aria-expanded={openTray === id}
                    aria-controls={`tray-${id}`}
                  >
                    {count} comment{count === 1 ? "" : "s"}
                  </button>
                </div>
                {/* Always here so aria-controls points at something that
                    exists, whichever way the tray is standing. */}
                <div className="tray" id={`tray-${id}`}>
                  {openTray === id && renderTray?.(id)}
                </div>
              </>
            )}
          </div>
        );
      })}
      {gutter.lifted !== null && gutter.dropIndex === others && <div className="drop-line" />}
    </div>
  );
}

function autosize(ta: HTMLTextAreaElement) {
  ta.style.height = "0px";
  ta.style.height = `${ta.scrollHeight}px`;
}

/** True when the caret sits inside a fence that has not been closed yet, in
 *  which case Return should insert a newline rather than split.
 *
 *  `$$` counts as a fence for the same reason ``` does: a display formula is
 *  one block whose content may contain blank lines, and splitting it at the
 *  caret would leave two halves of an equation that neither renders. */
function insideOpenFence(text: string, caret: number): boolean {
  const fences = text.slice(0, caret).match(/^\s*(```|~~~|\$\$)/gm);
  return (fences?.length ?? 0) % 2 === 1;
}

/** A textarea whose content fits on one visual line, so arrow keys move
 *  between paragraphs in one press for headings and short paragraphs, and
 *  leave wrapped paragraphs to the browser's own caret motion. */
function singleVisualLine(ta: HTMLTextAreaElement): boolean {
  const line = Number.parseFloat(getComputedStyle(ta).lineHeight) || 24;
  return ta.scrollHeight < line * 1.6;
}
